"use client";

import { PHONE_EXAMPLE } from "@/helpers/utils/phone";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { getFileType } from "@/helpers/utils/utility-functions";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import {
  AcceptTermsFormControl,
  FileOptions,
  FormControl,
  InputGroupType,
  OptionWithNote,
} from "@/models/form-types";
import { defaultRadioOptions } from "@/helpers/utils/form-constants";
import DOMPurify from "dompurify";
import validator from "validator";

type FormFieldPropType = {
  schema: { [x: string]: any };
  control: FormControl;
  fieldKey: string;
  index: number;
  totalQuestions: number;
  onSubmit: (data: any) => void;
  value: any;
  triggerSaveAndNext: boolean;
  onSubmitTriggered?: () => void;
};

// Example web address for URL questions, picked from the question itself.
function urlExample(fieldKey: string, label: string) {
  const text = `${fieldKey} ${label}`.toLowerCase();
  if (text.includes("linkedin")) return "https://www.linkedin.com/in/yourname";
  if (text.includes("github")) return "https://github.com/yourname";
  if (text.includes("instagram")) return "https://www.instagram.com/yourhandle";
  if (/video|trailer|youtube|vimeo/.test(text)) return "https://www.youtube.com/watch?v=yourvideo";
  if (/social|handle|tiktok|twitter|facebook/.test(text)) return "https://www.instagram.com/yourhandle";
  return "https://www.yourwebsite.com";
}

// Notes are plain strings; **bold** and [label](https://...) become markup.
function renderNote(note: string) {
  return note.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\(https:\/\/[^)\s]+\))/g).map((part, i) => {
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={i} className="font-semibold text-black">{bold[1]}</strong>;
    const link = part.match(/^\[([^\]]+)\]\((https:\/\/[^)\s]+)\)$/);
    if (link) {
      return (
        <a key={i} href={link[2]} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline underline-offset-2">
          {link[1]}
        </a>
      );
    }
    return part;
  });
}

export default function FormField({
  schema,
  control,
  fieldKey,
  index,
  totalQuestions,
  onSubmit,
  value,
  triggerSaveAndNext,
  onSubmitTriggered,
}: FormFieldPropType) {
  const form = useRef<HTMLFormElement>(null);
  const [fileName, setFileName] = useState("");
  const [isDragActive, setIsDragActive] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitted },
  } = useForm({
    resolver: zodResolver(z.object(schema)),
    defaultValues: { [fieldKey]: value },
  });


  useEffect(() => {
    const urlOptions: validator.IsURLOptions = {
      protocols: ['http', 'https'],
      require_protocol: true,
    };
    const fileFields = ["photo", "pitchDeckUpload", "logo", "teamLogo",
      "portfolio", "resume", "profilePhoto", "organizationLogo"
    ];

    if (fileFields.includes(fieldKey) && typeof value === 'string' && validator.isURL(value, urlOptions)) {
      const urlObj = new URL(value);
      const pathname = decodeURIComponent(urlObj.pathname);
      const filename = pathname && pathname.split('/').pop();
      setFileName(filename ?? "");
    }

    const defaultValues = { [fieldKey]: value };
    reset(defaultValues);

  }, [value, fieldKey, reset]);

  useEffect(() => {
    if (triggerSaveAndNext && form.current) {
      form.current.requestSubmit();
      onSubmitTriggered?.();
    }
  }, [triggerSaveAndNext, onSubmitTriggered]);


  const isUrlField = control.type === "url" || schema[fieldKey]?.description === "url";
  const isPhoneField = control.type === "phone" || schema[fieldKey]?.description === "phone";
  const example = control.example ?? (isUrlField ? urlExample(fieldKey, control.label) : isPhoneField ? PHONE_EXAMPLE : undefined);

  const renderInputField = () => {
    switch (control.type) {
      case "phone":
      case "email":
      case "url":
      case "text":
        return (
          <div className="mt-4 mx-1 flex flex-col justify-start items-start">
            <input
              autoFocus
              className={`w-full md:h-[3.45rem] h-[2.463rem] p-4 rounded-md text-base text-black placeholder:font-medium placeholder:text-placeholder-gray`}
              type="text"
              {...register(fieldKey)}
              placeholder={example ?? "Type your answer here"}
            />
            {errorBlock(fieldKey)}
          </div>
        );
      case "textarea":
        return (
          <div className="mt-4 mx-1 flex flex-col justify-start items-start ">
            <textarea
              className={`block w-full md:h-[8.125rem] h-[6.625rem] p-2 rounded-md text-base text-black text-start placeholder:font-medium placeholder:text-placeholder-gray overflow-auto`}
              {...register(fieldKey)}
              placeholder="Type your answer here"
              autoFocus
            ></textarea>
            {errorBlock(fieldKey)}
          </div>
        );
      case "checkbox":
        if (control.options) {
          return (
            <>
              <div className="flex justify-between flex-wrap gap-x-4 md:max-h-[50vh] mt-4 overflow-y-auto">
                {(control.options as string[])
                  .reduce<Array<string[]>>((acc, opt, i) => {
                    const groupIndex = Math.floor(i / 8);
                    if (!acc[groupIndex]) {
                      acc[groupIndex] = [];
                    }
                    acc[groupIndex].push(opt as string);
                    return acc;
                  }, [])
                  .map((group: any, groupIndex: number) => (
                    <div
                      key={groupIndex}
                      className="flex-col justify-start items-start mb-0"
                      style={{ minWidth: "min(18.125rem, 100%)", maxWidth: "min(36.875rem, 100%)" }}
                    >
                      {group &&
                        group.length > 0 &&
                        group.map((opt: any, index: number) => (
                          <div
                            key={`${fieldKey}-${groupIndex}-${index}`}
                            className="flex items-start justify-start mb-5"
                          >
                            <div className="flex items-center shrink-0">
                              <input
                                id={`${fieldKey}-${opt}`}
                                type="checkbox"
                                value={opt}
                                {...register(fieldKey)}
                              />
                            </div>
                            <label
                              htmlFor={`${fieldKey}-${opt}`}
                              className="ms-3 min-w-0 flex-1 font-medium text-base text-left leading-6 text-custom-gray whitespace-normal [overflow-wrap:anywhere]"
                            >
                              {opt}
                            </label>
                          </div>
                        ))}
                    </div>
                  ))}
              </div>
              {errorBlock(fieldKey)}
            </>
          );
        }
        break;
      case "checkbox-group":
        if (control.options) {
          return (
            <>
              <div className="flex flex-col flex-wrap md:max-h-[50vh] mt-4">
                {(control.options as InputGroupType[])
                  .reduce((arr, grp) => {
                    arr.push({ opt: grp.name, ischeckbox: false });
                    arr.push(
                      ...grp.options.map((opt) => ({
                        opt: opt,
                        ischeckbox: true,
                      }))
                    );
                    return arr;
                  }, [] as { opt: string; ischeckbox: boolean }[])
                  .map((itm, i) => (
                    <div key={`${fieldKey}-group-${i}`}>
                      {!itm.ischeckbox ? (
                        <div className="font-bold text-[0.938rem] text-left leading-6 text-custom-gray whitespace-normal mb-3 mr-3">
                          {itm.opt}
                        </div>
                      ) : (
                        <div className="flex items-start justify-start mb-3 mr-3">
                          <input
                            id={`${fieldKey}-${itm.opt}`}
                            type="checkbox"
                            value={itm.opt}
                            {...register(fieldKey)}
                          />
                          <label
                            htmlFor={`${fieldKey}-${itm.opt}`}
                            className="ms-3 font-medium text-base text-left leading-6 text-custom-gray whitespace-normal"
                          >
                            {itm.opt}
                          </label>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
              {errorBlock(fieldKey)}
            </>
          );
        }
        break;
      case "checkbox-with-note":
        if (control.options) {
          return (
            <>
              <div className="flex flex-row flex-wrap text-custom-gray md:max-h-[50vh] mt-4 overflow-y-auto">
                {(control.options as OptionWithNote<string>[]).map((opt, i) => (
                  <div
                    className={`w-full md:w-1/2 mb-3 ${i % 2 == 0 ? "pr-2" : ""
                      } `}
                    key={`${fieldKey}-${i}`}
                  >
                    <div className="flex items-start justify-start mb-3">
                      <input
                        id={`${fieldKey}-${opt.opt}`}
                        type="checkbox"
                        value={opt.opt}
                        {...register(fieldKey)}
                      />
                      <label
                        htmlFor={`${fieldKey}-${opt.opt}`}
                        className="ms-3 font-medium text-base text-left leading-6 text-custom-gray whitespace-normal"
                      >
                        {opt.opt}
                      </label>
                    </div>
                    <p className="text-sm text-left">{opt.note}</p>
                  </div>
                ))}
              </div>
              {errorBlock(fieldKey)}
            </>
          );
        }
        break;
      case "radio":
        let options = control.options;
        if (!options) {
          options = defaultRadioOptions.slice();
        }
        return (
          <>
            <div className="flex justify-between flex-wrap gap-x-8 mt-4">
              {(options as string[])
                .reduce<Array<string[]>>((acc, opt, i) => {
                  const groupIndex = Math.floor(i / 7);
                  if (!acc[groupIndex]) {
                    acc[groupIndex] = [];
                  }
                  acc[groupIndex].push(opt as string);
                  return acc;
                }, [])
                .map((group: any, groupIndex: number) => (
                  <div
                    key={groupIndex}
                    className="flex-col justify-start items-start mb-0"
                    style={{ minWidth: "min(18.125rem, 100%)", maxWidth: "min(36.875rem, 100%)" }}
                  >
                    {group &&
                      group.length > 0 &&
                      group.map((opt: any, index: number) => (
                        <div
                          key={`${fieldKey}-${groupIndex}-${index}`}
                          className="flex items-start justify-start mb-5"
                        >
                          <div className="flex items-center shrink-0">
                            <input
                              type="radio"
                              id={`${fieldKey}-${opt}`}
                              value={opt}
                              {...register(fieldKey)}
                            />
                          </div>
                          <label
                            htmlFor={`${fieldKey}-${opt}`}
                            className="ml-3 min-w-0 flex-1 font-medium text-base text-left leading-6 text-custom-gray whitespace-normal [overflow-wrap:anywhere]"
                          >
                            {opt}
                          </label>
                        </div>
                      ))}
                  </div>
                ))}
            </div>
            {errorBlock(fieldKey)}
          </>
        );
      case "file":
        return (
          <>
            <div className="flex flex-col justify-start items-start">
              <div
                onClick={() => {
                  form?.current?.[fieldKey].click();
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragActive(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragActive(false);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e: any) => {
                  e.preventDefault();
                  e.stopPropagation();

                  setIsDragActive(false);

                  const event = new Event("change", { bubbles: true });
                  if (form && form.current && form.current[fieldKey]) {
                    form.current[fieldKey].files = e.dataTransfer.files;
                    form.current[fieldKey].dispatchEvent(event);
                  }
                }}
                className={`${isDragActive
                  ? "bg-[#61A1E5] bg-opacity-20 border-[#61A1E5]"
                  : "border-black"
                  } relative flex w-full h-[8.438rem] mt-4 cursor-pointer items-center justify-center rounded-[10px] border-2 border-dashed border-black text-center`}
              >
                <div className="flex flex-col justify-center items-center">
                  <Image
                    height={32}
                    width={32}
                    className="mt-2"
                    alt="c-tribe"
                    src={
                      fileName
                        ? getFileType(fileName)
                          ? "/images/form/image_pick_icon.svg"
                          : "/images/form/file_icon.svg"
                        : "/images/form/upload_icon.svg"
                    }
                  />
                  {fileName ? (
                    <span className="text-sm text-black mt-2 ml-1">
                      {fileName}
                    </span>
                  ) : (
                    <div>
                      <span className="mt-2 block text-sm font-medium text-black">
                        <span className="font-bold">Click here</span> or
                        <span className="font-bold">drag</span> to upload
                      </span>
                      <span className="mt-1 block text-sm font-medium text-black">
                        Please use a
                        {(
                          control.options as FileOptions
                        )?.allowedFileExtentions?.map((ext, index) => (
                          <span key={index}>
                            {index > 0 && " or"}
                            <span className="font-bold">
                              &nbsp;.{ext.toUpperCase()}&nbsp;
                            </span>
                          </span>
                        ))}
                        file
                        <span className="font-bold">
                          &nbsp;(limit&nbsp;
                          {(control.options as FileOptions)?.maxFileSizeMB}MB)
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <input
                type="file"
                id="file"
                {...register(fieldKey, {
                  onChange: (e) => {
                    setFileName(e.target.value.split("\\").pop() ?? "");
                  },
                })}
                accept={(control.options as FileOptions)?.allowedFileExtentions?.map(x => `.${x}`).join(",")}
                style={{ display: "none" }}
              />

              {errors[fieldKey] && (
                <span className="text-sm text-red-500 mt-2 ml-1">
                  {errors[fieldKey]?.message?.toString()}
                </span>
              )}
            </div>
          </>
        );
      case "accept":
        return (
          <div style={{ maxWidth: 590 }}>
            <div
              className="flex justify-start items-start mb-6 gap-2"
              style={{ maxWidth: 590 }}
            >
              <span className="md:text-2xl text-lg font-semibold text-black text-left">
                {index}.
              </span>
              <span className="md:text-2xl text-lg font-semibold text-black text-left">
                {!!(control as AcceptTermsFormControl)?.termsAndconditions ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize((control as AcceptTermsFormControl)?.termsAndconditions ?? "") }}></div>
                ) : (
                  <>
                    By hitting &lsquo;I accept&rsquo;, I agree to receive email
                    communications (or phone if you&apos;ve given us your
                    number) from C-Tribe regarding the event and news. I also
                    agree, acknowledge, and verify that I have authority to
                    enter into the{" "}
                    <a
                      href="https://www.ctribefestival.com/terms-of-service"
                      className="text-blue-500"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Service
                    </a>{" "}
                    and have read the{" "}
                    <a
                      href="https://www.ctribefestival.com/privacy"
                      className="text-blue-500"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                    .
                  </>
                )}
              </span>
            </div>
            <div
              className="flex min-w-0 flex-col justify-start items-start mb-0 ml-8"
            >
              {defaultRadioOptions.map((opt, i) => (
                <div
                  key={`${fieldKey}-${i}`}
                  className="flex items-center justify-start mb-3"
                >
                  <input
                    type="radio"
                    id={`${fieldKey}-${opt}`}
                    value={opt}
                    {...register(fieldKey)}
                  />
                  <label
                    htmlFor={`${fieldKey}-${opt}`}
                    className="ms-3 font-medium text-base text-left leading-6 text-custom-gray whitespace-normal"
                  >
                    {opt == "Yes" ? "I Accept" : "I Don't Accept"}
                  </label>
                </div>
              ))}
              {errorBlock(fieldKey)}
            </div>
          </div>
        );
      default:
        return <></>;
    }
  };

  const errorBlock = (fieldKey: string) => {
    return (
      <>
        <div className="text-sm text-left text-red-500 mt-2">
          {errors[fieldKey] &&
            (errors[fieldKey]?.type != "invalid_type"
              ? `${errors[fieldKey]?.message?.toString()}`
              : isSubmitted && "Field is required")}
        </div>
      </>
    );
  };

  return (
    <>
      <form ref={form} onSubmit={handleSubmit(onSubmit)}>
        <div>
          {control.type !== "accept" ? (
            <div
              className={`flex justify-start items-start mb-2 gap-1 ${control.type === "textarea" ? "md:mt-10 mt-0" : "mt-0"
                } `}
              // style={{ maxWidth: control.label.length > 70 ? 599 : "100%" }}
              style={{ maxWidth: "100%" }}
            >
              <span className="md:text-2xl text-lg font-semibold text-black text-left">
                {index}.
              </span>
              <span className="md:text-2xl text-lg font-semibold text-black text-left pr-2">
                {control.label}
              </span>
            </div>
          ) : null}

          <div className={control.type === "accept" ? "ml-0" : "ml-5"}>
            {control.notes &&
              control.notes.filter(Boolean).map((note, i) => (
                <p
                  key={i}
                  className={
                    control.notesLayout === "spaced"
                      ? "text-left font-normal text-[17px] leading-7 text-gray-800 mt-4 first:mt-2"
                      : "text-left font-normal text-base leading-6 text-custom-gray mt-1"
                  }
                  style={{ maxWidth: "100%" }}
                >
                  {renderNote(note)}
                </p>
              ))}
            {example ? (
              <p className="text-left font-normal text-base leading-6 text-custom-gray mt-1">
                Example: <span className="font-medium text-black">{example}</span>
              </p>
            ) : null}
            <div className="w-full overflow-x-auto">{renderInputField()}</div>
          </div>

          <div className="ml-6 md:flex items-center hidden">
            <button
              className=" h-[1.875rem] flex justify-center items-center bg-black text-white text-xs leading-4 px-5 mt-6 mr-4 rounded-[2.5rem] mb-6 hover:bg-white hover:text-black hover:border hover:border-gray-300"
              type="submit"
            >
              {index >= totalQuestions ? "SUBMIT" : "Next"}
            </button>
            <Image
              height={14}
              width={80}
              alt="c-tribe"
              src="/images/form/enter_icon.svg"
            />
          </div>
        </div>
      </form>
    </>
  );
}
